import {
  FieldPath,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc
} from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import {
  SEO_COLLECTION,
  SEO_DOCUMENTS,
  SEO_ENTITY_DOCUMENTS,
  SEO_IMAGE_MAX_SIZE_BYTES,
  SEO_STORAGE_ROOT,
  createEmptySeoState,
  createEntitySeoMap,
  createGlobalSeo,
  createSeoBlock,
  createSeoPagesMap,
  createSeoScript,
  createSeoScriptsList
} from '../data/seoModel'
import { firestore, storage } from './firebaseClient'

const SEO_ENTITY_COLLECTION_KEYS = Object.keys(SEO_ENTITY_DOCUMENTS)

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function seoDocRef(documentId) {
  return doc(firestore, SEO_COLLECTION, documentId)
}

function assertEntityCollection(collectionKey) {
  if (!SEO_ENTITY_COLLECTION_KEYS.includes(collectionKey)) {
    throw new Error(`SEO сущностей коллекции ${collectionKey} не описано в модели данных.`)
  }
}

function safePathPart(value) {
  return String(value || 'item')
    .trim()
    .replace(/[^a-zA-Z0-9-_]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'item'
}

function fileExtension(file) {
  const name = file.name || ''
  const extension = name.includes('.') ? name.split('.').pop() : ''
  return extension ? `.${extension.toLowerCase()}` : ''
}

/**
 * Дефолтная структура шести документов коллекции SiteSeo.
 * Используется только для сидинга: существующие значения не перезаписываются.
 */
function defaultSeoDocuments() {
  return {
    [SEO_DOCUMENTS.global]: createGlobalSeo(),
    [SEO_DOCUMENTS.pages]: { pages: createSeoPagesMap() },
    [SEO_DOCUMENTS.scripts]: { items: [] },
    [SEO_DOCUMENTS.entitiesServices]: { items: {} },
    [SEO_DOCUMENTS.entitiesProducts]: { items: {} },
    [SEO_DOCUMENTS.entitiesArticles]: { items: {} }
  }
}

/**
 * Собирает патч только из отсутствующих полей: существующие значения
 * не читаются как «неправильные» и не переписываются.
 */
function collectMissingFields(defaults, current, prefix = '') {
  const patch = {}
  Object.entries(defaults).forEach(([key, defaultValue]) => {
    const path = prefix ? `${prefix}.${key}` : key
    const currentValue = isPlainObject(current) ? current[key] : undefined

    if (currentValue === undefined) {
      patch[path] = clone(defaultValue)
      return
    }
    if (isPlainObject(defaultValue) && isPlainObject(currentValue)) {
      Object.assign(patch, collectMissingFields(defaultValue, currentValue, path))
    }
  })
  return patch
}

export async function ensureSeoDocuments() {
  const defaults = defaultSeoDocuments()
  const created = []
  const patched = []

  await Promise.all(Object.entries(defaults).map(async ([documentId, defaultData]) => {
    const documentRef = seoDocRef(documentId)
    const snapshot = await getDoc(documentRef)

    if (!snapshot.exists()) {
      await setDoc(documentRef, { ...clone(defaultData), updatedAt: serverTimestamp() }, { merge: true })
      created.push(documentId)
      return
    }

    const patch = collectMissingFields(defaultData, snapshot.data())
    if (!Object.keys(patch).length) return
    await updateDoc(documentRef, patch)
    patched.push(documentId)
  }))

  return { created, patched }
}

export async function loadSeo() {
  const snapshot = await getDocs(collection(firestore, SEO_COLLECTION))
  const documents = {}
  snapshot.docs.forEach(item => {
    documents[item.id] = item.data()
  })

  const state = createEmptySeoState()
  state.global = createGlobalSeo(documents[SEO_DOCUMENTS.global])
  state.pages = createSeoPagesMap((documents[SEO_DOCUMENTS.pages] || {}).pages)
  state.scripts = createSeoScriptsList((documents[SEO_DOCUMENTS.scripts] || {}).items)
  SEO_ENTITY_COLLECTION_KEYS.forEach(collectionKey => {
    const documentData = documents[SEO_ENTITY_DOCUMENTS[collectionKey]] || {}
    state.entities[collectionKey] = createEntitySeoMap(documentData.items)
  })
  state.existingDocumentIds = Object.keys(documents)
  return state
}

export async function saveSeoGlobal(payload) {
  const globalSeo = createGlobalSeo(payload)
  await setDoc(seoDocRef(SEO_DOCUMENTS.global), {
    ...globalSeo,
    updatedAt: serverTimestamp()
  }, { merge: true })
  return globalSeo
}

export async function saveSeoPage(pageKey, block) {
  const seoBlock = createSeoBlock(block)
  await setDoc(seoDocRef(SEO_DOCUMENTS.pages), {
    pages: { [pageKey]: seoBlock },
    updatedAt: serverTimestamp()
  }, { merge: true })
  return seoBlock
}

/**
 * Точечная запись SEO одной сущности: обновляется только ключ items.<entityId>,
 * остальные сущности в документе не трогаются. block === null удаляет ключ.
 */
export async function saveSeoEntity(collectionKey, entityId, block) {
  assertEntityCollection(collectionKey)
  const documentRef = seoDocRef(SEO_ENTITY_DOCUMENTS[collectionKey])
  const snapshot = await getDoc(documentRef)
  if (!snapshot.exists()) {
    await setDoc(documentRef, { items: {}, updatedAt: serverTimestamp() }, { merge: true })
  }

  const value = block === null ? deleteField() : createSeoBlock(block)
  await updateDoc(documentRef, new FieldPath('items', entityId), value, 'updatedAt', serverTimestamp())
  return block === null ? null : value
}

export async function saveSeoScripts(items) {
  const scripts = (Array.isArray(items) ? items : []).map(createSeoScript)
  await setDoc(seoDocRef(SEO_DOCUMENTS.scripts), {
    items: scripts,
    updatedAt: serverTimestamp()
  }, { merge: true })
  return scripts
}

function readImageSize(file) {
  return new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      resolve({ width: image.naturalWidth || 0, height: image.naturalHeight || 0 })
      URL.revokeObjectURL(objectUrl)
    }
    image.onerror = () => {
      resolve({ width: 0, height: 0 })
      URL.revokeObjectURL(objectUrl)
    }
    image.src = objectUrl
  })
}

/**
 * scope: 'global' -> seo/global/<file>
 * scope: 'page'   -> seo/pages/<pageKey>/<file>
 * scope: 'entity' -> seo/entities/<collection>/<entityId>/<file>
 * Старые файлы не удаляются — ссылка просто перезаписывается.
 */
function seoStoragePath(scope, options, filename) {
  if (scope === 'page') return `${SEO_STORAGE_ROOT}/pages/${safePathPart(options.pageKey)}/${filename}`
  if (scope === 'entity') {
    return `${SEO_STORAGE_ROOT}/entities/${safePathPart(options.collectionKey)}/${safePathPart(options.entityId)}/${filename}`
  }
  return `${SEO_STORAGE_ROOT}/global/${filename}`
}

export async function uploadSeoImage(file, scope, options = {}) {
  if (file.size > SEO_IMAGE_MAX_SIZE_BYTES) {
    throw new Error('Картинка больше 5 МБ — мессенджеры обрежут такой файл.')
  }

  const filename = `${Date.now()}-${safePathPart(file.name).slice(0, 80)}${fileExtension(file)}`
  const storageRef = ref(storage, seoStoragePath(scope, options, filename))
  const [size] = await Promise.all([
    readImageSize(file),
    uploadBytes(storageRef, file, { contentType: file.type || 'image/*' })
  ])
  const url = await getDownloadURL(storageRef)

  return {
    url,
    width: size.width,
    height: size.height,
    type: file.type || ''
  }
}
